import { useState, useEffect } from 'react'
import { useAction } from 'convex/react'
import { api } from '../../../../convex/_generated/api'
import {
  Loader2,
  GitCommit,
  Rocket,
  CheckCircle2,
  XCircle,
  Clock,
  ArrowUpCircle,
  ChevronDown,
  Copy,
  Check,
  GitBranch,
  User,
  Calendar,
  Hash,
  Server,
} from 'lucide-react'
import { cn } from '@/lib/utils'

interface Commit {
  sha: string
  message: string
  author: string
  avatarUrl: string | null
  date: string
  url: string
}

interface Deployment {
  id: string
  status: string
  serviceName: string
  createdAt: string
  commitMessage: string | null
  commitHash: string | null
  commitAuthor: string | null
  url: string
}

function timeAgo(dateStr: string) {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  return new Date(dateStr).toLocaleDateString()
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function getStatusConfig(status: string) {
  switch (status) {
    case 'SUCCESS':
      return { label: 'Success', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500', icon: CheckCircle2 }
    case 'FAILED':
    case 'CRASHED':
      return { label: status === 'FAILED' ? 'Failed' : 'Crashed', bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', dot: 'bg-red-500', icon: XCircle }
    case 'BUILDING':
      return { label: 'Building', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500 animate-pulse', icon: Clock }
    case 'DEPLOYING':
      return { label: 'Deploying', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-500 animate-pulse', icon: ArrowUpCircle }
    case 'REMOVING':
      return { label: 'Removing', bg: 'bg-neutral-50', text: 'text-neutral-600', border: 'border-neutral-200', dot: 'bg-neutral-400', icon: Clock }
    default:
      return { label: status, bg: 'bg-neutral-50', text: 'text-neutral-600', border: 'border-neutral-200', dot: 'bg-neutral-400', icon: Clock }
  }
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      onClick={(e) => {
        e.stopPropagation()
        navigator.clipboard.writeText(text)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      }}
      className="p-1 rounded hover:bg-neutral-200 transition-colors"
      title="Copy SHA"
    >
      {copied ? <Check className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3 text-neutral-400" />}
    </button>
  )
}

function CommitCard({ commit }: { commit: Commit }) {
  const [expanded, setExpanded] = useState(false)
  const [title, ...rest] = commit.message.split('\n')
  const body = rest.join('\n').trim()

  return (
    <div
      className={cn(
        'rounded-lg border border-neutral-200 bg-white transition-all duration-200',
        expanded ? 'shadow-md ring-1 ring-neutral-100' : 'hover:border-neutral-300 hover:shadow-sm'
      )}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left p-3.5 flex items-start gap-3"
      >
        {commit.avatarUrl ? (
          <img
            src={commit.avatarUrl}
            alt={commit.author}
            className="w-8 h-8 rounded-full shrink-0 mt-0.5"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-linear-to-br from-neutral-100 to-neutral-200 flex items-center justify-center shrink-0 mt-0.5">
            <User className="h-4 w-4 text-neutral-500" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-neutral-900 truncate">{title}</p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs text-neutral-500">{commit.author}</span>
            <span className="text-xs text-neutral-300">&middot;</span>
            <span className="text-xs text-neutral-400">{timeAgo(commit.date)}</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0 mt-0.5">
          <span className="text-[11px] font-mono text-neutral-400 bg-neutral-100 px-1.5 py-0.5 rounded">
            {commit.sha.slice(0, 7)}
          </span>
          <ChevronDown className={cn(
            'h-4 w-4 text-neutral-400 transition-transform duration-200',
            expanded && 'rotate-180'
          )} />
        </div>
      </button>

      {expanded && (
        <div className="border-t border-neutral-100 px-3.5 pb-3.5">
          {body && (
            <div className="mt-3 p-2.5 bg-neutral-50 rounded-md">
              <p className="text-xs text-neutral-600 whitespace-pre-wrap font-mono leading-relaxed">{body}</p>
            </div>
          )}
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2">
              <Hash className="h-3.5 w-3.5 text-neutral-400" />
              <span className="text-xs font-mono text-neutral-600">{commit.sha.slice(0, 12)}</span>
              <CopyButton text={commit.sha} />
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="h-3.5 w-3.5 text-neutral-400" />
              <span className="text-xs text-neutral-600">{formatDate(commit.date)}</span>
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <a
              href={commit.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-neutral-900 text-white text-xs font-medium hover:bg-neutral-800 transition-colors"
            >
              <GitCommit className="h-3.5 w-3.5" />
              View on GitHub
            </a>
            <a
              href={commit.url.replace('/commit/', '/tree/')}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-neutral-200 text-neutral-700 text-xs font-medium hover:bg-neutral-50 transition-colors"
            >
              <GitBranch className="h-3.5 w-3.5" />
              Browse files
            </a>
          </div>
        </div>
      )}
    </div>
  )
}

function DeploymentCard({ deployment }: { deployment: Deployment }) {
  const [expanded, setExpanded] = useState(false)
  const statusConfig = getStatusConfig(deployment.status)
  const title = deployment.commitMessage?.split('\n')[0] ?? 'No commit message'

  return (
    <div
      className={cn(
        'rounded-lg border bg-white transition-all duration-200',
        expanded ? 'shadow-md ring-1 ring-neutral-100 border-neutral-200' : 'border-neutral-200 hover:border-neutral-300 hover:shadow-sm'
      )}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left p-3.5 flex items-start gap-3"
      >
        <div className={cn(
          'w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5',
          statusConfig.bg,
        )}>
          <statusConfig.icon className={cn('h-4 w-4', statusConfig.text)} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-neutral-900 truncate">{title}</p>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <span className={cn(
              'inline-flex items-center gap-1.5 text-[11px] font-medium',
              statusConfig.text,
            )}>
              <span className={cn('w-1.5 h-1.5 rounded-full', statusConfig.dot)} />
              {statusConfig.label}
            </span>
            <span className="text-xs text-neutral-300">&middot;</span>
            <span className="text-xs text-neutral-500">{deployment.serviceName}</span>
            <span className="text-xs text-neutral-300">&middot;</span>
            <span className="text-xs text-neutral-400">{timeAgo(deployment.createdAt)}</span>
          </div>
        </div>
        <ChevronDown className={cn(
          'h-4 w-4 text-neutral-400 shrink-0 mt-1 transition-transform duration-200',
          expanded && 'rotate-180'
        )} />
      </button>

      {expanded && (
        <div className="border-t border-neutral-100 px-3.5 pb-3.5">
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2">
              <Server className="h-3.5 w-3.5 text-neutral-400" />
              <span className="text-xs text-neutral-600">{deployment.serviceName}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="h-3.5 w-3.5 text-neutral-400" />
              <span className="text-xs text-neutral-600">{formatDate(deployment.createdAt)}</span>
            </div>
            {deployment.commitHash && (
              <div className="flex items-center gap-2">
                <Hash className="h-3.5 w-3.5 text-neutral-400" />
                <span className="text-xs font-mono text-neutral-600">{deployment.commitHash.slice(0, 12)}</span>
                <CopyButton text={deployment.commitHash} />
              </div>
            )}
            {deployment.commitAuthor && (
              <div className="flex items-center gap-2">
                <User className="h-3.5 w-3.5 text-neutral-400" />
                <span className="text-xs text-neutral-600">{deployment.commitAuthor}</span>
              </div>
            )}
          </div>
          <div className={cn(
            'mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium',
            statusConfig.bg, statusConfig.text, statusConfig.border,
          )}>
            <statusConfig.icon className="h-3.5 w-3.5" />
            {statusConfig.label}
          </div>
          <div className="mt-3 flex gap-2">
            <a
              href={deployment.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-neutral-900 text-white text-xs font-medium hover:bg-neutral-800 transition-colors"
            >
              <Rocket className="h-3.5 w-3.5" />
              View on Railway
            </a>
            {deployment.commitHash && (
              <a
                href={`https://github.com/GenerativePlatforms/footage-app/commit/${deployment.commitHash}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-neutral-200 text-neutral-700 text-xs font-medium hover:bg-neutral-50 transition-colors"
              >
                <GitCommit className="h-3.5 w-3.5" />
                View commit
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default function AdminCommitsPage() {
  const getCommits = useAction(api.adminActions.getCommits)
  const getDeployments = useAction(api.adminActions.getDeployments)
  const [commits, setCommits] = useState<Commit[]>([])
  const [deployments, setDeployments] = useState<Deployment[]>([])
  const [loadingCommits, setLoadingCommits] = useState(true)
  const [loadingDeployments, setLoadingDeployments] = useState(true)

  useEffect(() => {
    getCommits()
      .then(setCommits)
      .catch((e) => console.error('[commits] Failed to load:', e))
      .finally(() => setLoadingCommits(false))

    getDeployments()
      .then(setDeployments)
      .catch((e) => console.error('[deployments] Failed to load:', e))
      .finally(() => setLoadingDeployments(false))
  }, [])

  return (
    <div className="h-full flex flex-col">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 flex-1 min-h-0">
        {/* Commits Column */}
        <div className="flex flex-col min-h-0 rounded-xl border border-neutral-200 bg-neutral-50/50">
          <div className="flex items-center gap-3 px-4 py-3 border-b border-neutral-200 bg-white rounded-t-xl">
            <div className="w-7 h-7 rounded-lg bg-neutral-900 flex items-center justify-center">
              <GitCommit className="h-3.5 w-3.5 text-white" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-neutral-900">GitHub Commits</h2>
              <p className="text-[11px] text-neutral-400">
                {loadingCommits ? 'Loading...' : `${commits.length} recent`}
              </p>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {loadingCommits ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
              </div>
            ) : commits.length === 0 ? (
              <div className="text-center py-20 text-neutral-500">
                <div className="w-12 h-12 rounded-full bg-neutral-100 flex items-center justify-center mx-auto mb-3">
                  <GitCommit className="h-6 w-6 text-neutral-300" />
                </div>
                <p className="text-sm font-medium">No commits found</p>
                <p className="text-xs mt-1 text-neutral-400">Make sure GITHUB_TOKEN is set in Convex env</p>
              </div>
            ) : (
              commits.map((commit) => (
                <CommitCard key={commit.sha} commit={commit} />
              ))
            )}
          </div>
        </div>

        {/* Deployments Column */}
        <div className="flex flex-col min-h-0 rounded-xl border border-neutral-200 bg-neutral-50/50">
          <div className="flex items-center gap-3 px-4 py-3 border-b border-neutral-200 bg-white rounded-t-xl">
            <div className="w-7 h-7 rounded-lg bg-neutral-900 flex items-center justify-center">
              <Rocket className="h-3.5 w-3.5 text-white" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-neutral-900">Railway Deployments</h2>
              <p className="text-[11px] text-neutral-400">
                {loadingDeployments ? 'Loading...' : `${deployments.length} recent`}
              </p>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {loadingDeployments ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
              </div>
            ) : deployments.length === 0 ? (
              <div className="text-center py-20 text-neutral-500">
                <div className="w-12 h-12 rounded-full bg-neutral-100 flex items-center justify-center mx-auto mb-3">
                  <Rocket className="h-6 w-6 text-neutral-300" />
                </div>
                <p className="text-sm font-medium">No deployments found</p>
                <p className="text-xs mt-1 text-neutral-400">Make sure RAILWAY_API_TOKEN is set in Convex env</p>
              </div>
            ) : (
              deployments.map((deployment) => (
                <DeploymentCard key={deployment.id} deployment={deployment} />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
